import { useEffect, useState, useCallback } from "react";
import { Waves, X, RefreshCw, FileText, ChevronLeft, Power } from "lucide-react";
import api from "@/lib/api";

const Stat = ({ label, value, tone }) => (
  <div className="rounded-lg bg-slate-800/50 border border-slate-700/50 px-2.5 py-1.5">
    <div className={`font-mono text-lg font-bold tabular-nums ${tone || "text-white"}`}>{value}</div>
    <div className="text-[9px] uppercase tracking-wider text-slate-500 leading-tight">{label}</div>
  </div>
);

const V = ({ level }) => {
  const c = { V0: "text-slate-400 bg-slate-700/40", V1: "text-cyan-300 bg-cyan-500/15", V2: "text-emerald-300 bg-emerald-500/15", V3: "text-amber-300 bg-amber-500/15" }[level] || "";
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${c}`}>{level}</span>;
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-0.5 border-b border-slate-800/40 last:border-0">
    <span className="text-[10px] font-mono uppercase text-slate-500">{k}</span>
    <span className="text-xs text-slate-200 text-right">{v ?? "—"}</span>
  </div>
);

export const PilotPanel = ({ onClose, onFocusIncident }) => {
  const [dash, setDash] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, i] = await Promise.all([api.pilotState(), api.pilotIncidents()]);
      setDash(d);
      setIncidents(i.incidents || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const run = async () => { setBusy(true); try { await api.pilotRun(); await load(); } finally { setBusy(false); } };
  const toggle = async () => { if (dash) { await api.pilotToggle(!dash.tracking_on); load(); } };
  const openDetail = async (id) => { try { setDetail(await api.pilotIncident(id)); } catch { /* */ } };
  const report = async (id) => {
    const r = await api.pilotReport(id);
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = 555;
    let y = 48;
    const line = (t, size = 10, bold = false, color = [30, 30, 30]) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const wrapped = doc.splitTextToSize(String(t), W);
      wrapped.forEach((w) => { if (y > 800) { doc.addPage(); y = 48; } doc.text(w, 40, y); y += size + 4; });
    };
    const kv = (k, v) => line(`${k}: ${v ?? "—"}`, 10);
    doc.setFillColor(11, 31, 58); doc.rect(0, 0, 595, 30, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("SlickTrace — Incident Report", 40, 20);
    line(r.incident_id, 16, true, [11, 31, 58]); y += 4;
    kv("AOI", r.aoi_id); kv("State", r.state); kv("Verification", r.verification);
    kv("Potential source", r.potential_source); kv("Treatment status", r.treatment_status);
    kv("First detected", r.first_detected); kv("Last observed", r.last_observed);
    y += 6; line("Observations", 12, true, [11, 31, 58]);
    (r.observations || []).forEach((o) => {
      line(`${o.label}  ${o.acq_ts || ""}  area ${o.area_km2} km²  frags ${o.fragments}  conf ${o.machine_confidence ?? "—"}  [${o.cerulean_id}]`, 9);
    });
    const cm = r.change_metrics;
    if (cm) {
      y += 6; line("Verified change", 12, true, [11, 31, 58]);
      kv("Elapsed", `${cm.elapsed_hours} h`);
      kv("Area change", cm.area_change_pct != null ? `${cm.area_change_pct}%` : "—");
      kv("Movement", `${cm.centroid_move_km} km ${cm.direction} (${cm.bearing_deg}°)`);
      kv("Observed drift", cm.drift_rate_kmh != null ? `${cm.drift_rate_kmh} km/hr` : "—");
      kv("Forecast error", cm.forecast_error_km != null ? `${cm.forecast_error_km} km` : "—");
      kv("Fragments", `${cm.fragments_from} → ${cm.fragments_to}`);
      kv("Surface assessment", cm.surface_assessment);
      kv("Comparability", cm.comparability);
    }
    y += 6; line("Sentinel-1 scenes", 12, true, [11, 31, 58]);
    if ((r.sentinel_scenes || []).length === 0) line("Awaiting next satellite pass.", 9);
    (r.sentinel_scenes || []).forEach((s) => line(`${s.scene_id}  ${s.acq_time || ""}  ${s.orbit || ""}`, 8));
    y += 6; line("Data sources", 12, true, [11, 31, 58]);
    (r.data_sources || []).forEach((s) => line(`• ${s}`, 9));
    y += 6; line("Methodology & disclaimer", 12, true, [11, 31, 58]);
    line(r.methodology_disclaimer, 8, false, [90, 90, 90]);
    line(`Generated ${r.generated_at}`, 8, false, [130, 130, 130]);
    doc.save(`${id}.pdf`);
  };

  const cm = detail?.change_metrics;

  return (
    <aside data-testid="pilot-panel" className="absolute top-20 left-4 z-30 w-[340px] max-h-[calc(100vh-8rem)] flex flex-col rounded-xl bg-slate-900/97 backdrop-blur-xl border border-cyan-500/30 shadow-2xl overflow-hidden st-fade">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/80">
        <div className="flex items-center gap-2">
          <Waves className="h-4 w-4 text-cyan-400" />
          <span className="font-display text-sm font-bold text-white">Oil Slick Tracking — Vietnam Pilot</span>
        </div>
        <button data-testid="pilot-close" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>
      </div>

      {!detail && (
        <div className="flex-1 overflow-y-auto st-scroll p-3 space-y-3">
          <div className="flex items-center gap-2">
            <button data-testid="pilot-toggle" onClick={toggle} className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-mono ${dash?.tracking_on ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-slate-700/40 text-slate-400 border border-slate-600"}`}>
              <Power className="h-3.5 w-3.5" /> {dash?.tracking_on ? "Tracking ON" : "Tracking OFF"}
            </button>
            <button data-testid="pilot-run" onClick={run} disabled={busy} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-mono bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Run pipeline
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Stat label="Active" value={dash?.active_incidents ?? "—"} />
            <Stat label="New 24h" value={dash?.new_24h ?? "—"} tone="text-cyan-300" />
            <Stat label="Awaiting reobs" value={dash?.awaiting_reobs ?? "—"} />
            <Stat label="Reobserved" value={dash?.reobserved ?? "—"} tone="text-emerald-300" />
            <Stat label="Reducing" value={dash?.reducing ?? "—"} tone="text-pink-300" />
            <Stat label="Expanding" value={dash?.expanding ?? "—"} tone="text-amber-300" />
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono">
            <V level="V0" />{dash?.V0 ?? 0}<V level="V1" />{dash?.V1 ?? 0}<V level="V2" />{dash?.V2 ?? 0}<V level="V3" />{dash?.V3 ?? 0}
          </div>
          {dash?.errors?.length > 0 && (
            <p data-testid="pilot-errors" className="text-[10px] font-mono text-amber-400">DATA UPDATE DELAYED · {dash.errors[dash.errors.length - 1].msg?.slice(0, 60)}</p>
          )}
          <div className="space-y-1.5">
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">Incidents ({incidents.length})</span>
            {incidents.length === 0 && <p className="text-xs text-slate-500 font-mono py-3 text-center">No incidents yet — run the pipeline.</p>}
            {incidents.map((i) => (
              <button key={i.incident_id} data-testid={`incident-item-${i.incident_id}`} onClick={() => openDetail(i.incident_id)}
                className="w-full text-left rounded-lg border border-slate-800/70 bg-slate-800/40 px-3 py-2 hover:bg-slate-800/70">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono text-white">{i.incident_id}</span>
                  <V level={i.verification} />
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] font-mono text-slate-500">
                  <span>{i.state}</span>·<span>{i.observation_count} obs</span>·<span>{i.latest_area} km²</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {detail && (
        <div className="flex-1 overflow-y-auto st-scroll p-3 space-y-3">
          <button data-testid="pilot-back" onClick={() => setDetail(null)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"><ChevronLeft className="h-3.5 w-3.5" /> Back</button>
          <div className="flex items-center justify-between">
            <span className="font-mono text-base font-bold text-white">{detail.incident_id}</span>
            <V level={detail.verification} />
          </div>
          <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
            <Row k="State" v={detail.state} />
            <Row k="First detected" v={detail.observations?.[0]?.acq_ts?.slice(0, 16)} />
            <Row k="Last observed" v={detail.observations?.[detail.observations.length - 1]?.acq_ts?.slice(0, 16)} />
            <Row k="Potential source" v={detail.potential_source} />
            <Row k="Treatment" v={detail.treatment_status} />
            <Row k="Next satellite" v={detail.sentinel_scenes?.length ? detail.sentinel_scenes[0].acq_time?.slice(0, 16) : "awaiting pass"} />
          </div>
          {cm && (
            <div data-testid="incident-change" className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
              <div className="text-[10px] font-mono uppercase text-cyan-400 py-1">Verified change {cm.from}→{cm.to}</div>
              <Row k="Area change" v={cm.area_change_pct != null ? `${cm.area_change_pct}%` : "—"} />
              <Row k="Movement" v={`${cm.centroid_move_km} km ${cm.direction}`} />
              <Row k="Drift rate" v={cm.drift_rate_kmh != null ? `${cm.drift_rate_kmh} km/hr` : "—"} />
              <Row k="Forecast error" v={cm.forecast_error_km != null ? `${cm.forecast_error_km} km` : "—"} />
              <Row k="Fragments" v={`${cm.fragments_from} → ${cm.fragments_to}`} />
              <Row k="Elapsed" v={`${cm.elapsed_hours} h`} />
              <Row k="Assessment" v={cm.surface_assessment} />
              <Row k="Comparability" v={cm.comparability} />
            </div>
          )}
          <div className="text-[10px] font-mono text-slate-500">
            Forecast: {detail.forecasts?.[detail.forecasts.length - 1]?.label} · {detail.observations?.length} observation(s)
          </div>
          <div className="flex gap-2">
            <button data-testid="incident-report" onClick={() => report(detail.incident_id)} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-mono bg-cyan-500/15 text-cyan-300 border border-cyan-500/40">
              <FileText className="h-3.5 w-3.5" /> Incident report
            </button>
            {onFocusIncident && detail.observations?.[0]?.centroid && (
              <button onClick={() => onFocusIncident(detail)} className="rounded-lg px-2.5 py-1.5 text-xs font-mono bg-slate-700/40 text-slate-300 border border-slate-600">Show on map</button>
            )}
          </div>
          <p className="text-[9px] leading-relaxed font-mono text-slate-600">
            Predicted Surface Drift (indicative synthetic environment). Surface reduction is not proof of treatment; V0–V2 do not confirm mineral oil.
          </p>
        </div>
      )}
    </aside>
  );
};

export default PilotPanel;
